// Централизованный выход из аккаунта.
//
// Раньше logout дублировался в profile-dropdown и settings-shell и чистил
// только session-store + bearer-токен. Персистнутый TanStack Query кэш
// ["auth","me"] (localStorage key studyhub:query-cache) переживал reload и
// «возвращал» пользователя в аккаунт: auth.me гидратировался из кэша до
// сетевого запроса → UI показывал авторизованного, но protected-разделы
// падали 401 («Не удалось загрузить»), т.к. cookie уже отозван. Здесь — полная
// очистка клиентского состояния. См. memory note про logout-баг 2026-06-04.

import { useAuthExpiredStore } from "@/lib/auth-expired-store";
import { resetIdentityCaches } from "@/lib/identity-caches";
import { disableNativePush } from "@/lib/native-push";
import { captureClientError } from "@/lib/observability";
import { disconnectAndClearPowerSync, hasPendingUploads } from "@/lib/powersync/client";
import { suspendPowerSyncAuth } from "@/lib/powersync/jwt-provider";
import { clearAllAttempts, clearSyncFailuresForOwner } from "@/lib/powersync/upload-failures";
import { broadcastAuthEvent } from "@/providers/auth-provider";
import { useSessionStore } from "@/stores/session";
import { authClient } from "./auth-client";
import { queryClient } from "./query-client";

// Должен совпадать с key персистера в query-client.ts.
const QUERY_CACHE_PERSIST_KEY = "studyhub:query-cache";

export async function performSignOut(): Promise<void> {
	// До revoke: иначе 401 от in-flight запросов откроет «Сессия истекла».
	useAuthExpiredStore.getState().suppress();

	// 0. Кэш PowerSync-JWT — ПЕРВЫМ делом и синхронно. Иначе silent-refresh,
	//    стартовавший до выхода, успевал положить в кэш токен уходящего юзера, и
	//    следующий вход поднимал синк под чужой личностью: сервис отвергал токен
	//    401, SDK уходил в вечный retry, а полоска «Синхронизация библиотеки…»
	//    висела до перезагрузки страницы.
	//
	//    `suspend`, а не просто сброс кэша: поток синка живёт в SharedWorker и
	//    после выхода продолжает просить учётные данные. Без остановки каждая
	//    такая просьба уходила в сеть и возвращалась 401 — консоль после
	//    разлогина заливало красным.
	suspendPowerSyncAuth();

	// 0a. Module-level кэши вкладки (presigned-ссылки на иллюстрации, артефакты,
	//     вложения; озвучка реплик). Синхронно и здесь же, а не только реакцией
	//     `AuthProvider` на смену id: между `authClient.logout()` и ре-рендером
	//     провайдера успевает пройти навигация, и уходящий пользователь оставил бы
	//     живые ссылки следующему. Идемпотентно — повторный сброс безвреден.
	resetIdentityCaches();

	// 0b. Токен push-устройства — пока ещё жив bearer. Только в приложении; на вебе
	//     функция сразу возвращается.
	await disableNativePush();

	// 1. Server-side revoke (сессия + cookie) + clear bearer. Не падаем на
	//    сетевой ошибке — локальную сессию чистим в любом случае.
	const userId = useSessionStore.getState().user?.id;
	await authClient.logout().catch(() => undefined);

	if (userId) {
		await clearSyncFailuresForOwner(userId).catch(() => undefined);
	}
	clearAllAttempts();

	// 2. Локальная SQLite чистится ТОЛЬКО здесь — на явном выходе, чтобы
	//    следующий пользователь устройства не увидел чужие данные. Потеря
	//    неотправленной очереди в этот момент осознанная, но не должна быть
	//    невидимой: фиксируем в телеметрии.
	if (await hasPendingUploads().catch(() => false)) {
		captureClientError(new Error("logout: локальная очередь не пуста, данные будут стёрты"), {
			reason: "explicit_sign_out",
		});
	}
	await disconnectAndClearPowerSync().catch(() => undefined);
	// Отметку владельца базы (`studyhub.powersync.owner`) НЕ снимаем намеренно:
	// если очистка выше не доехала (закрыли вкладку, упал WASM), запись остаётся
	// единственным свидетельством, что база чужая, и следующий вход другим
	// аккаунтом вычистит её сам — см. connectPowerSyncForUser.

	// 3. Сброс session-store → isAuthenticated=false. AuthProvider уводит в
	//    гостя, PowerSyncProvider отключается (уже без очистки — см. п.2).
	useSessionStore.getState().logout();

	// 4. Очистка in-memory кэша TanStack Query — auth.me + все protected-данные
	//    (materials/notes/...), чтобы не остался чужой/устаревший контент.
	queryClient.clear();

	// 5. Удаление персистнутого снапшота — иначе reload гидратирует старого
	//    юзера из localStorage до сетевого auth.me (корень бага «после refresh
	//    снова авторизован, но разделы не грузятся»).
	try {
		window.localStorage.removeItem(QUERY_CACHE_PERSIST_KEY);
	} catch {
		// приватный режим / квота — не критично
	}

	// 6. Синхронизация выхода между вкладками.
	broadcastAuthEvent("logout");

	// 7. Прокрутка в начало страницы.
	//
	// Роутер сбрасывает её только при СМЕНЕ пути (`main.tsx`, `scrollRestoration`),
	// а выходят чаще всего прямо с главной: путь остаётся `/`, дашборд под
	// пользователем подменяется гостевым лендингом, и человек оказывается в
	// середине чужой страницы (замечание владельца 2026-08-16). Прокрутка здесь,
	// а не в обработчике кнопки, потому что точек выхода несколько (меню
	// профиля, отказ от согласия, отзыв сессии) — и все они проходят через эту
	// функцию.
	try {
		window.scrollTo({ top: 0, left: 0, behavior: "auto" });
	} catch {
		// нет window (тест/SSR-подобная среда) — прокручивать нечего
	}
}
